import { useCallback, useState, useEffect } from "react";
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
} from '@stripe/react-stripe-js';
import { supabase } from '../lib/supabase';
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Rocket, ArrowLeft, ShieldCheck } from "lucide-react";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

export const CheckoutForm = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isRedirected, setIsRedirected] = useState(false);

  useEffect(() => {
    // Check if user was redirected from login page
    if (state?.redirected_from === 'login') {
      setIsRedirected(true);
    }
  }, [state]);

  if (state === null || state === undefined) {
    window.location.replace(window.location.origin);
    return null;
  }
  
  const fetchClientSecret = useCallback(() => {
    const customer_email = state["customer_email"]
    setIsLoading(true);
    return supabase.functions.invoke('checkouthack2', {
      headers: {"content-type": "application/json"}, 
      body: {"customer_email": customer_email}
    })
      .then((clientSecret) => {
        setIsLoading(false);
        return clientSecret.data["clientSecret"];
      });
  }, [state]);

  const options = {
    fetchClientSecret,
    // Remove the appearance object as it's not supported by EmbeddedCheckoutProvider
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Decorative background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-indigo-900/20 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-purple-900/20 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 left-1/2 w-64 h-64 bg-blue-900/20 rounded-full filter blur-3xl"></div>
      </div>
      
      {/* Header */}
      <header className="relative z-10 py-6 border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <motion.button
                onClick={() => navigate(-1)}
                className="mr-4 p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ArrowLeft className="h-5 w-5" />
              </motion.button>
              <h1 className="text-2xl font-bold text-white flex items-center">
                <Rocket className="h-6 w-6 text-indigo-400 mr-3" />
                Complete Your Subscription
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Welcome message for redirected users */}
      {isRedirected && (
        <motion.div 
          className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-6"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="bg-indigo-900/40 border border-indigo-800 rounded-lg p-4 backdrop-blur-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <ShieldCheck className="h-5 w-5 text-indigo-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-indigo-300">
                  Welcome! One more step needed
                </h3>
                <div className="mt-2 text-sm text-indigo-200">
                  <p>
                    You've successfully signed in, but you need an active subscription to access the dashboard. 
                    Complete the payment below to get started.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
      
      {/* Main content */}
      <main className="relative z-10 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="bg-gray-800 rounded-2xl overflow-hidden shadow-xl border border-gray-700"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {isLoading && (
              <div className="flex items-center justify-center py-16">
                <div className="relative w-12 h-12">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-12 w-12 rounded-full border-t-2 border-b-2 border-indigo-500 animate-spin"></div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Rocket className="h-6 w-6 text-indigo-500" />
                  </div>
                </div>
              </div>
            )}
            
            <EmbeddedCheckoutProvider
              stripe={stripePromise}
              options={{
                fetchClientSecret,
                onComplete: () => navigate('/dashboard')
              }}
            >
              <EmbeddedCheckout className="py-10 px-6"/>
            </EmbeddedCheckoutProvider>
          </motion.div>
          
          {/* Security notice */}
          <motion.div 
            className="mt-8 flex items-center justify-center text-gray-400 text-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <ShieldCheck className="h-4 w-4 mr-2 text-indigo-400" />
            <span>Secure checkout powered by Stripe</span>
          </motion.div>
          
          {/* Additional information */}
          <motion.div 
            className="mt-8 bg-gray-800/80 rounded-xl p-6 border border-gray-700 text-gray-300 text-sm leading-relaxed backdrop-blur-sm"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <h3 className="font-medium text-white mb-2">What happens next?</h3>
            <p className="mb-2">After completing your payment, you'll get immediate access to all features included in your subscription.</p>
            <p>If you have any questions or need assistance, please contact us at <a href="mailto:support@research-x.com" className="text-indigo-400 hover:text-indigo-300">support@research-x.com</a>.</p>
          </motion.div>
        </div>
      </main>
    </div>
  );
}